import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import invariant from 'tiny-invariant';
import i18n from 'i18n-js';

import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import Columns from './Columns';
import ScrollableBoard from './ScrollableBoard';

import { createRegistry } from '@v2/pages/planboard/components/Board/web/registry';
import { BoardContext } from './context';
import {
  PlanboardState,
  selectSelectedMachienGroupId,
  selectLastRescheduleError,
  selectLastCloseError,
  selectPendingOperations,
  selectExternalMoveDetected,
  selectBackendRescheduleDetected,
  setPendingOperation,
  removePendingOperation,
  clearRescheduleError,
  clearCloseError,
  clearExternalMoveDetected,
  clearBackendRescheduleDetected,
  setReschedulingFromPosition,
  clearReschedulingByCorrelationId,
} from '../../../../../../../../reducers/v2/planboard';

import type { BoardContextValue, BoardState, ColumnMap, ColumnType, Outcome, PendingOperation, Trigger } from '@v2/pages/planboard/components/Board/web/types';
import { preserveOptimisticPositions } from './helpers';
import { Machine } from '@interfaces/machines';
import { backendCall } from '@reducers/utils';
import { Alert } from '@helpers';
import { selectMachines, selectMachinesGroup } from '@reducers/machines';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

const BoardContextProvider = () => {
  const dispatch = useAppDispatch();
  const machineGroups = useSelector(selectMachinesGroup);
  const machines = useSelector(selectMachines) as Machine[];
  const machineProductionOperations = useSelector<{ planboard: PlanboardState }, PlanboardState['machineProductionOperations']>(
    (state) => state.planboard.machineProductionOperations
  );
  const selectedMachineGroupId = useSelector(selectSelectedMachienGroupId);
  const lastRescheduleError = useSelector(selectLastRescheduleError);
  const lastCloseError = useSelector(selectLastCloseError);
  const pendingOperations = useSelector(selectPendingOperations);
  const externalMoveDetected = useSelector(selectExternalMoveDetected);
  const backendRescheduleDetected = useSelector(selectBackendRescheduleDetected);

  const [data, setData] = useState<BoardState>({ columnMap: {}, lastOperation: null, orderedColumnIds: [], pendingOperationIds: [] });
  const [isScrolled, setIsScrolled] = useState(false);

  const stableData = useRef(data);
  // Single Map for operationId-first tracking (replaces three separate Maps)
  const pendingOperationsRef = useRef<Map<number, PendingOperation>>(new Map());
  const registry = useMemo(createRegistry, []);
  const instanceId = useMemo(() => Symbol('instance-id'), []);
  const scheduleMove = useCallback(
    async (payload: {
      correlationId: string;
      productionOperationId: number;
      machineId: number;
      precedingProductionOperationId?: number;
      subsequentProductionOperationId?: number;
    }) => {
      try {
        const call = {
          method: (api: any) => api.productionInformation.scheduleProductionOperation,
          params: payload,
        };
        await dispatch(backendCall(call));
        const machine = machines.find((m: Machine) => m.machineId === payload.machineId);
        // TODO: for now leave here as is;
        trackEvent(TRACKING_EVENTS.PLANBOARD, {
          action: 'jobMoved',
          machineId: String(payload.machineId || ''),
          machineName: machine?.shortName || machine?.fullName || '',
        });
      } catch (error) {
        console.error('Failed to schedule production operation', error);
        throw new Error();
      }
    },
    [dispatch]
  );
  const addPending = useCallback((id: number) => {
    setData((curr) => ({
      ...curr,
      pendingOperationIds: curr.pendingOperationIds.includes(id) ? curr.pendingOperationIds : curr.pendingOperationIds.concat(id),
    }));
  }, []);
  const removePending = useCallback((id: number) => {
    setData((curr) => ({
      ...curr,
      pendingOperationIds: curr.pendingOperationIds.filter((pendingId) => pendingId !== id),
    }));
  }, []);

  // start drag operation handler
  const handleDragOperation = useCallback(
    (operationId: number): { correlationId: string; cleanup: () => void } => {
      const existing = pendingOperationsRef.current.get(operationId);
      const newCorrelationId = crypto.randomUUID();
      const ignoredCorrelationIds = new Set<string>();

      // if re-dragging, add old correlationId to ignored list
      if (existing) {
        clearTimeout(existing.timeoutId);
        ignoredCorrelationIds.add(existing.correlationId);
        existing.ignoredCorrelationIds.forEach((id) => ignoredCorrelationIds.add(id));
      }

      // Set up timeout for auto-cleanup
      const timeoutId = setTimeout(() => {
        pendingOperationsRef.current.delete(operationId);
        dispatch(removePendingOperation(operationId));
        dispatch(clearReschedulingByCorrelationId(newCorrelationId));
        removePending(operationId);
      }, 20000);

      // Store in local ref
      pendingOperationsRef.current.set(operationId, {
        correlationId: newCorrelationId,
        timestamp: Date.now(),
        timeoutId,
        ignoredCorrelationIds,
      });

      // Dispatch to Redux
      addPending(operationId);
      dispatch(
        setPendingOperation({
          operationId,
          correlationId: newCorrelationId,
          ignoredCorrelationIds: Array.from(ignoredCorrelationIds),
          isOwnDrag: true,
        })
      );

      // Return cleanup function for error handling
      const cleanup = () => {
        clearTimeout(timeoutId);
        pendingOperationsRef.current.delete(operationId);
        dispatch(removePendingOperation(operationId));
        removePending(operationId);
      };

      return { correlationId: newCorrelationId, cleanup };
    },
    [dispatch, addPending, removePending]
  );

  useEffect(() => {
    stableData.current = data;
  }, [data]);

  const getColumns = useCallback(() => {
    const { columnMap, orderedColumnIds } = stableData.current;
    return orderedColumnIds.map((columnId) => columnMap[columnId]);
  }, []);

  const reorderCard = useCallback(
    ({
      columnId,
      startIndex,
      finishIndex,
      trigger = 'keyboard',
    }: {
      columnId: number;
      startIndex: number;
      finishIndex: number;
      trigger?: Trigger;
    }) => {
      setData((data) => {
        const sourceColumn = data.columnMap[columnId];
        const updatedItems = reorder({
          list: sourceColumn.items,
          startIndex,
          finishIndex,
        });

        const updatedSourceColumn: ColumnType = {
          ...sourceColumn,
          items: updatedItems,
        };

        const updatedMap: ColumnMap = {
          ...data.columnMap,
          [columnId]: updatedSourceColumn,
        };

        const outcome: Outcome | null = {
          type: 'card-reorder',
          columnId,
          startIndex,
          finishIndex,
        };

        return {
          ...data,
          columnMap: updatedMap,
          lastOperation: {
            trigger: trigger,
            outcome,
          },
          pendingOperationIds: data.pendingOperationIds,
        };
      });
    },
    []
  );

  const moveCard = useCallback(
    ({
      startColumnId,
      finishColumnId,
      itemIndexInStartColumn,
      itemIndexInFinishColumn,
      trigger = 'keyboard',
    }: {
      startColumnId: number;
      finishColumnId: number;
      itemIndexInStartColumn: number;
      itemIndexInFinishColumn?: number;
      trigger?: 'pointer' | 'keyboard';
    }) => {
      // invalid cross column movement
      if (startColumnId === finishColumnId) {
        return;
      }
      setData((data) => {
        const sourceColumn = data.columnMap[startColumnId];
        const destinationColumn = data.columnMap[finishColumnId];
        const item = sourceColumn.items[itemIndexInStartColumn];

        const destinationItems = Array.from(destinationColumn.items);

        const newIndexInDestination = itemIndexInFinishColumn ?? 0;
        destinationItems.splice(newIndexInDestination, 0, item);

        const updatedMap = {
          ...data.columnMap,
          [startColumnId]: {
            ...sourceColumn,
            items: sourceColumn.items.filter((i) => i.productionOperationId !== item.productionOperationId),
          },
          [finishColumnId]: {
            ...destinationColumn,
            items: destinationItems,
          },
        };

        const outcome: Outcome | null = {
          type: 'card-move',
          finishColumnId,
          itemIndexInStartColumn,
          itemIndexInFinishColumn: newIndexInDestination,
        };

        return {
          ...data,
          columnMap: updatedMap,
          lastOperation: {
            outcome,
            trigger: trigger,
          },
          pendingOperationIds: data.pendingOperationIds,
        };
      });
    },
    []
  );

  const buildColumnsFromStore = useCallback(() => {
    const machinesIds: [number, ColumnType][] = machines.reduce<[number, ColumnType][]>((acc, machine) => {
      const shouldSkip =
        typeof selectedMachineGroupId === 'number' &&
        !machine.machineGroups.some((machineGroup) => machineGroup.machineGroupId === selectedMachineGroupId);

      if (!shouldSkip) {
        acc.push([
          machine.machineId,
          { title: machine.shortName, columnId: machine.machineId, items: machineProductionOperations[machine.machineId]?.items ?? [] },
        ]);
      }

      return acc;
    }, []);

    const allItems: [number, ColumnType][] = [
      [-1, { title: i18n.t('No Machine'), columnId: -1, items: machineProductionOperations[-1]?.items ?? [] }],
    ];
    const columnMap: ColumnMap = Object.fromEntries(allItems.concat(machinesIds));

    const orderedColumnIds = Object.keys(columnMap)
      .reverse()
      .map((key) => +key);

    preserveOptimisticPositions(columnMap, pendingOperationsRef, stableData.current?.columnMap);

    setData((prev) => ({ columnMap, orderedColumnIds, lastOperation: null, pendingOperationIds: prev.pendingOperationIds }));
  }, [i18n, machineProductionOperations, machines, selectedMachineGroupId]);

  const moveToPosition = useCallback(
    async (itemId: number, columnId: number, targetPosition: number) => {
      const column = stableData.current.columnMap[columnId];
      if (!column) return;

      const items = column.items;
      const currentIndex = items.findIndex((i) => i.productionOperationId === itemId);
      if (currentIndex === -1) return;

      const targetIndex = targetPosition - 1; // Convert 1-based to 0-based

      // Skip if already at position
      if (currentIndex === targetIndex) return;

      // Calculate preceding/subsequent operation IDs after the move
      const reorderedItems = [...items];
      const [movedItem] = reorderedItems.splice(currentIndex, 1);
      reorderedItems.splice(targetIndex, 0, movedItem);

      const preceding = reorderedItems[targetIndex - 1]?.productionOperationId;
      const subsequent = reorderedItems[targetIndex + 1]?.productionOperationId;

      // Optimistic UI update
      reorderCard({ columnId, startIndex: currentIndex, finishIndex: targetIndex, trigger: 'pointer' });

      // Use operationId-first helper for correlation management
      const { correlationId, cleanup } = handleDragOperation(itemId);

      try {
        await scheduleMove({
          correlationId,
          productionOperationId: itemId,
          machineId: columnId,
          ...(preceding ? { precedingProductionOperationId: preceding } : {}),
          ...(subsequent ? { subsequentProductionOperationId: subsequent } : {}),
        });
      } catch (e) {
        cleanup();
        buildColumnsFromStore();
      }
    },
    [reorderCard, handleDragOperation, scheduleMove, buildColumnsFromStore]
  );

  useEffect(() => {
    buildColumnsFromStore();
  }, [machineGroups, machineProductionOperations, selectedMachineGroupId, machines, buildColumnsFromStore]);

  const { lastOperation } = data;

  useEffect(() => {
    if (lastOperation === null) {
      return;
    }
    const { outcome, trigger } = lastOperation;

    if (outcome.type === 'column-reorder') {
      const { finishIndex } = outcome;

      const { columnMap, orderedColumnIds } = stableData.current;
      const sourceColumn = columnMap[orderedColumnIds[finishIndex]];

      const entry = registry.getColumn(sourceColumn.columnId);
      triggerPostMoveFlash(entry.element);

      return;
    }

    if (outcome.type === 'card-reorder') {
      const { columnId, finishIndex } = outcome;

      const { columnMap } = stableData.current;
      const column = columnMap[columnId];
      const item = column.items[finishIndex];

      const entry = registry.getCard(item.productionOperationId);
      triggerPostMoveFlash(entry.element);

      if (trigger !== 'keyboard') {
        return;
      }

      return;
    }

    if (outcome.type === 'card-move') {
      const { finishColumnId, itemIndexInFinishColumn } = outcome;

      const data = stableData.current;
      const destinationColumn = data.columnMap[finishColumnId];
      const item = destinationColumn.items[itemIndexInFinishColumn];

      const entry = registry.getCard(item.productionOperationId);
      triggerPostMoveFlash(entry.element);

      if (trigger !== 'keyboard') {
        return;
      }

      return;
    }
  }, [lastOperation, registry]);

  useEffect(() => {
    return combine(
      monitorForElements({
        canMonitor({ source }) {
          return source.data.instanceId === instanceId;
        },
        async onDrop(args) {
          const { location, source } = args;
          if (!location.current.dropTargets.length) {
            return;
          }
          // 1. remove element from original position
          // 2. move to new position

          // Dragging a card
          if (source.data.type === 'card') {
            const itemId = source.data.itemId;
            invariant(typeof itemId === 'number');
            // find the originating column from the initial targets
            const startColumnRecord = location.initial.dropTargets.find((target) => typeof target.data.columnId === 'number');
            invariant(startColumnRecord, 'expected a column drop target for the drag start');
            const sourceId = startColumnRecord.data.columnId;
            invariant(typeof sourceId === 'number');
            const sourceColumn = data.columnMap[sourceId];
            const itemIndex = sourceColumn.items.findIndex((item) => item.productionOperationId === itemId);
            const movedItem = sourceColumn.items[itemIndex];

            if (!movedItem) {
              return;
            }

            if (location.current.dropTargets.length === 1) {
              const [destinationColumnRecord] = location.current.dropTargets;
              const destinationId = destinationColumnRecord.data.columnId;
              invariant(typeof destinationId === 'number');
              const destinationColumn = data.columnMap[destinationId];
              invariant(destinationColumn);

              // dropping onto the same column without a specific target card -> no-op
              if (sourceColumn === destinationColumn) {
                return;
              }

              const destinationIndex = destinationColumn.items.length;
              moveCard({
                itemIndexInStartColumn: itemIndex,
                startColumnId: sourceColumn.columnId,
                finishColumnId: destinationColumn.columnId,
                itemIndexInFinishColumn: destinationIndex,
                trigger: 'pointer',
              });

              const sourceItems = [...sourceColumn.items];
              sourceItems.splice(itemIndex, 1);
              const destinationItems = [...destinationColumn.items].filter((item) => item.productionOperationId !== itemId);
              destinationItems.splice(destinationIndex, 0, movedItem);

              const preceding = destinationItems[destinationIndex - 1]?.productionOperationId;
              const subsequent = destinationItems[destinationIndex + 1]?.productionOperationId;

              // Use operationId-first helper for correlation management
              const { correlationId, cleanup } = handleDragOperation(itemId);

              // Track position-based loading on source column for cross-column move
              dispatch(setReschedulingFromPosition({ machineId: sourceColumn.columnId, fromIndex: itemIndex, correlationId }));

              try {
                await scheduleMove({
                  correlationId,
                  productionOperationId: itemId,
                  machineId: destinationColumn.columnId,
                  ...(preceding ? { precedingProductionOperationId: preceding } : {}),
                  ...(subsequent ? { subsequentProductionOperationId: subsequent } : {}),
                });
              } catch (e) {
                cleanup();
                dispatch(clearReschedulingByCorrelationId(correlationId));
                buildColumnsFromStore();
              }
              return;
            }

            // dropping in a column (relative to a card)
            if (location.current.dropTargets.length === 2) {
              const [destinationCardRecord, destinationColumnRecord] = location.current.dropTargets;
              const destinationColumnId = destinationColumnRecord.data.columnId;
              invariant(typeof destinationColumnId === 'number');
              const destinationColumn = data.columnMap[destinationColumnId];

              const indexOfTarget = destinationColumn.items.findIndex(
                (item) => item.productionOperationId === destinationCardRecord.data.productionOperationId
              );
              const closestEdgeOfTarget: Edge | null = extractClosestEdge(destinationCardRecord.data);

              // case 1: ordering in the same column
              if (sourceColumn === destinationColumn) {
                const destinationIndex = getReorderDestinationIndex({
                  startIndex: itemIndex,
                  indexOfTarget,
                  closestEdgeOfTarget,
                  axis: 'vertical',
                });
                if (destinationIndex === itemIndex) {
                  return;
                }
                reorderCard({
                  columnId: sourceColumn.columnId,
                  startIndex: itemIndex,
                  finishIndex: destinationIndex,
                  trigger: 'pointer',
                });

                const sourceItems = [...sourceColumn.items];
                sourceItems.splice(itemIndex, 1);
                const destinationItems = sourceItems;
                destinationItems.splice(destinationIndex, 0, movedItem);

                const preceding = destinationItems[destinationIndex - 1]?.productionOperationId;
                const subsequent = destinationItems[destinationIndex + 1]?.productionOperationId;

                // Use operationId-first helper for correlation management
                const { correlationId, cleanup } = handleDragOperation(itemId);

                try {
                  await scheduleMove({
                    correlationId,
                    productionOperationId: itemId,
                    machineId: destinationColumn.columnId,
                    ...(preceding ? { precedingProductionOperationId: preceding } : {}),
                    ...(subsequent ? { subsequentProductionOperationId: subsequent } : {}),
                  });
                } catch (e) {
                  cleanup();
                  buildColumnsFromStore();
                }
                return;
              }

              // case 2: moving into a new column relative to a card

              const destinationIndex = closestEdgeOfTarget === 'bottom' ? indexOfTarget + 1 : indexOfTarget;
              if (sourceColumn === destinationColumn && destinationIndex === itemIndex) {
                return;
              }

              moveCard({
                itemIndexInStartColumn: itemIndex,
                startColumnId: sourceColumn.columnId,
                finishColumnId: destinationColumn.columnId,
                itemIndexInFinishColumn: destinationIndex,
                trigger: 'pointer',
              });

              const sourceItems = [...sourceColumn.items];
              sourceItems.splice(itemIndex, 1);
              const destinationItems = [...destinationColumn.items].filter((item) => item.productionOperationId !== itemId);
              destinationItems.splice(destinationIndex, 0, movedItem);

              const preceding = destinationItems[destinationIndex - 1]?.productionOperationId;
              const subsequent = destinationItems[destinationIndex + 1]?.productionOperationId;

              // Use operationId-first helper for correlation management
              const { correlationId, cleanup } = handleDragOperation(itemId);

              // Track position-based loading on source column for cross-column move
              dispatch(setReschedulingFromPosition({ machineId: sourceColumn.columnId, fromIndex: itemIndex, correlationId }));

              try {
                await scheduleMove({
                  correlationId,
                  productionOperationId: itemId,
                  machineId: destinationColumn.columnId,
                  ...(preceding ? { precedingProductionOperationId: preceding } : {}),
                  ...(subsequent ? { subsequentProductionOperationId: subsequent } : {}),
                });
              } catch (e) {
                cleanup();
                dispatch(clearReschedulingByCorrelationId(correlationId));
                buildColumnsFromStore();
              }
            }
          }
        },
      })
    );
  }, [data, instanceId, moveCard, reorderCard, scheduleMove, handleDragOperation, buildColumnsFromStore, dispatch]);

  // Clean up local state when SignalR events successfully process operations (operationId-first)
  useEffect(() => {
    // Sync local state with Redux pendingOperations
    const localOperationIds = Array.from(pendingOperationsRef.current.keys());

    localOperationIds.forEach((operationId) => {
      if (!pendingOperations[operationId]) {
        // Operation was removed from Redux (processed by SignalR) - clean up local state
        const existing = pendingOperationsRef.current.get(operationId);
        if (existing?.timeoutId) {
          clearTimeout(existing.timeoutId);
        }
        pendingOperationsRef.current.delete(operationId);
        removePending(operationId);
      }
    });
  }, [pendingOperations, removePending]);

  // Handle reschedule errors from SignalR
  useEffect(() => {
    if (lastRescheduleError) {
      // Find which operation has this correlation ID
      const operationEntry = Array.from(pendingOperationsRef.current.entries()).find(
        ([, pending]) => pending.correlationId === lastRescheduleError.correlationId
      );

      if (operationEntry) {
        const [operationId, pending] = operationEntry;
        const errorMessage = lastRescheduleError.errorMessage || i18n.t('Failed to reschedule operation');
        Alert({ title: errorMessage, type: 'danger' });
        console.error('Reschedule failed:', errorMessage);

        // Clean up tracking
        clearTimeout(pending.timeoutId);
        pendingOperationsRef.current.delete(operationId);
        removePending(operationId);

        buildColumnsFromStore();
      }
      dispatch(clearRescheduleError());
    }
  }, [lastRescheduleError, buildColumnsFromStore, removePending, dispatch, i18n]);

  // Handle when another user moves a card we're tracking
  useEffect(() => {
    if (externalMoveDetected) {
      const { operationId } = externalMoveDetected;
      const localOp = pendingOperationsRef.current.get(operationId);

      if (localOp) {
        Alert({
          title: i18n.t('Another user moved this card'),
          type: 'warning',
        });

        // Clean up our tracking since another user took over
        clearTimeout(localOp.timeoutId);
        pendingOperationsRef.current.delete(operationId);
        removePending(operationId);
      }

      dispatch(clearExternalMoveDetected());
    }
  }, [externalMoveDetected, dispatch, removePending, i18n]);

  // Handle when backend reschedule job affects a card we're tracking
  useEffect(() => {
    if (backendRescheduleDetected) {
      const { operationId } = backendRescheduleDetected;
      const localOp = pendingOperationsRef.current.get(operationId);

      if (localOp) {
        Alert({
          title: i18n.t('Schedule was updated by system'),
          type: 'info',
        });

        // Clean up our tracking
        clearTimeout(localOp.timeoutId);
        pendingOperationsRef.current.delete(operationId);
        removePending(operationId);
      }

      dispatch(clearBackendRescheduleDetected());
    }
  }, [backendRescheduleDetected, dispatch, removePending, i18n]);

  // Handle close production operation errors
  useEffect(() => {
    if (lastCloseError) {
      let errorMessage: string;

      if (lastCloseError.validationErrors?.length) {
        errorMessage = lastCloseError.validationErrors.map((e) => e.errorMessage).join('\n');
      } else {
        errorMessage = lastCloseError.errorMessage || i18n.t('Failed to mark job as finished');
      }

      Alert({ title: errorMessage, type: 'danger' });
      // Refetch to restore the card since the close operation failed
      buildColumnsFromStore();
      dispatch(clearCloseError());
    }
  }, [lastCloseError, dispatch, buildColumnsFromStore]);

  const contextValue: BoardContextValue = useMemo(() => {
    return {
      getColumns,
      reorderCard,
      moveCard,
      moveToPosition,
      pendingOperationIds: data.pendingOperationIds,
      registerCard: registry.registerCard,
      registerColumn: registry.registerColumn,
      instanceId,
      isScrolled,
      setIsScrolled,
    };
  }, [getColumns, reorderCard, registry, moveCard, moveToPosition, instanceId, data.pendingOperationIds, isScrolled]);

  return (
    <BoardContext.Provider value={contextValue}>
      <ScrollableBoard>
        <Columns boardData={data} />
      </ScrollableBoard>
    </BoardContext.Provider>
  );
};

export default BoardContextProvider;
